<template>
  <v-sheet class="pa-4">
    <p>
      <v-btn class="mr-5" @click="createRelatedPersonDialog">
        <v-icon class="mr-1" small>mdi-plus</v-icon>
        {{ $t('ITEM.ADD') }} {{ $t('RELATED_PERSONS.ITEM') }}
      </v-btn>
      <v-btn @click="createRelatedCompanyDialog">
        <v-icon class="mr-1" small>mdi-plus</v-icon>
        {{ $t('ITEM.ADD') }} {{ $t('RELATED_COMPANIES.ITEM') }}
      </v-btn>
    </p>

    <span class="text-h5">{{ $t('PROFILE.RELATIONS_WITH_PERSONS') }}</span>

    <related-list
      v-bind="relatedPersonsListData"
      :options.sync="options.persons"
      @show="showRelatedPerson"
    />

    <div
      v-if="
        buildingComplexId && relatedCommonCompaniesListData.relations.length
      "
    >
      <span class="text-h5">{{
        `${$t('PROFILE.RELATIONS_WITH_COMPANIES')} (${$t('BUILDING_COMPLEXES.ITEM')})`
      }}</span>

      <related-list
        v-bind="relatedCommonCompaniesListData"
        :options.sync="options.commonCompanies"
        type="c"
        @show="showRelatedCompany"
      />
    </div>

    <span class="text-h5">{{
      `${$t('PROFILE.RELATIONS_WITH_COMPANIES')} (${$t('PROFILE.PRIVATE')})`
    }}</span>

    <related-list
      v-bind="relatedCompaniesListData"
      :options.sync="options.companies"
      @show="showRelatedCompany"
    />

    <v-dialog v-if="dialog" v-model="dialog" persistent max-width="800">
      <component
        :is="component.name"
        v-bind="component.data"
        :errors="errors.getAll()"
        @create="createRelated"
        @update="updateRelated"
        @remove="removeRelated"
        @cancel="cancelDialog"
        @update-prop="clearErrorByName($event)"
      />
    </v-dialog>

    <v-dialog
      v-if="dialogDeleteRelatedPerson"
      v-model="dialogDeleteRelatedPerson"
      persistent
      max-width="400px"
    >
      <delete-related-person
        :id="relation.id"
        @cancel="dialogDeleteRelatedPerson = !dialogDeleteRelatedPerson"
        @success="deletedRelatedPerson"
      ></delete-related-person>
    </v-dialog>
  </v-sheet>
</template>

<script>
import RelatedList from '@/modules/relations/components/RelatedList';
import { get } from 'lodash';
import DeleteRelatedPerson from '@/components/relations/DeleteRelatedPerson';
import PersonRelatedCompaniesService from '@/services/relations/person-related-companies.service';
import PersonRelatedPersonsService from '@/services/relations/person-related-persons.service';
import errors from '@/modules/_global/mixins/errors';
import DataOptions from '@/modules/_global/classes/DataOptions';
import BuildingComplexRelatedCompaniesService from '@/services/relations/building-complex-related-companies.service';

export default {
  name: 'Relations',

  components: {
    RelatedList,
    CreateRelatedCompany: () =>
      import('@/modules/relations/components/CreateRelatedCompany'),
    ShowRelatedCompany: () =>
      import('@/modules/relations/components/ShowRelatedCompany'),
    CreateRelatedPerson: () =>
      import('@/components/relations/CreateRelatedPerson'),
    ShowRelatedPerson: () => import('@/components/relations/EditRelatedPerson'),

    DeleteRelatedPerson
  },

  mixins: [errors],

  props: {
    person: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    options: {
      persons: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['priority'],
        sortDesc: [false],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false
      },
      companies: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['priority'],
        sortDesc: [false],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false
      },
      commonCompanies: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['priority'],
        sortDesc: [false],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false
      }
    },
    relatedCompaniesListData: {
      relations: [],
      meta: {}
    },
    relatedCommonCompaniesListData: {
      relations: [],
      meta: {}
    },
    relatedPersonsListData: {
      relations: [],
      meta: {}
    },
    dialog: false,
    component: null,

    relation: null,
    buildingComplexId: null,
    dialogCreateRelatedPerson: false,
    dialogEditRelatedPerson: false,
    dialogDeleteRelatedPerson: false
  }),

  computed: {
    personName() {
      return `${this.person.first_name} ${this.person.last_name}`;
    },
    preparedPersonOptions() {
      return DataOptions.createFromObject(
        this.options.persons
      ).toBackendFormat();
    },
    preparedCompanyOptions() {
      return DataOptions.createFromObject(
        this.options.companies
      ).toBackendFormat();
    },
    preparedCommonCompanyOptions() {
      return DataOptions.createFromObject(
        this.options.commonCompanies
      ).toBackendFormat();
    }
  },

  watch: {
    options: {
      deep: true,
      immediate: true,
      handler() {
        this.refreshData();
      }
    },
    buildingComplexId: {
      deep: true,
      immediate: true,
      handler() {
        this.getDataCommonRelatedCompanies();
      }
    },
    person: {
      deep: true,
      immediate: true,
      handler() {
        this.buildingComplexId = get(
          this.person,
          'inhabitant.apartment.building.building_complex_id',
          null
        );
      }
    }
  },

  created() {
    // this.relationsWithPersons = this.person.related_persons;
    // this.relationsWithCompanies = this.person.related_companies;
  },

  methods: {
    getDataCommonRelatedCompanies() {
      if (!this.buildingComplexId) {
        return;
      }
      BuildingComplexRelatedCompaniesService.getList(
        this.buildingComplexId,
        this.preparedCommonCompanyOptions
      ).then(({ data }) => {
        this.relatedCommonCompaniesListData.relations = data.data;
        this.relatedCommonCompaniesListData.meta = data.meta;
      });
    },

    getDataRelatedCompanies() {
      PersonRelatedCompaniesService.getList(
        this.person.id,
        this.preparedPersonOptions
      ).then(({ data }) => {
        this.relatedCompaniesListData.relations = data.data;
        this.relatedCompaniesListData.meta = data.meta;
      });
    },

    getDataRelatedPersons() {
      PersonRelatedPersonsService.getList(
        this.person.id,
        this.preparedCompanyOptions
      ).then(({ data }) => {
        this.relatedPersonsListData.relations = data.data;
        this.relatedPersonsListData.meta = data.meta;
      });
    },

    createRelatedCompanyDialog() {
      this.dialog = true;
      this.component = {
        name: 'CreateRelatedCompany',
        service: PersonRelatedCompaniesService,
        data: {
          name: this.personName
        }
      };
    },

    createRelatedPersonDialog() {
      this.dialog = true;
      this.component = {
        name: 'CreateRelatedPerson',
        service: PersonRelatedPersonsService,
        data: {
          name: this.personName
        }
      };
    },

    showRelatedCompany(v) {
      PersonRelatedCompaniesService.getById(this.person.id, v.related_id)
        .then(({ data }) => {
          this.dialog = true;
          this.component = {
            name: 'ShowRelatedCompany',
            service: PersonRelatedCompaniesService,
            data: {
              name: this.personName,
              value: { ...data }
            }
          };
        })
        .catch(console.error);
    },

    showRelatedPerson(v) {
      PersonRelatedPersonsService.getById(this.person.id, v.related_id)
        .then(({ data }) => {
          this.dialog = true;
          this.component = {
            name: 'ShowRelatedPerson',
            service: PersonRelatedPersonsService,
            data: {
              name: this.personName,
              value: { ...data }
            }
          };
        })
        .catch(console.error);
    },

    cancelDialog() {
      this.dialog = false;
      this.component = null;
    },

    cancelDialogAndRefresh() {
      this.refreshData();
      this.cancelDialog();
    },

    refreshData() {
      this.getDataRelatedCompanies();
      this.getDataRelatedPersons();
      this.getDataCommonRelatedCompanies();
    },

    createRelated(form) {
      this.component.service
        .create(this.person.id, form)
        .then(({ data }) => {
          this.cancelDialogAndRefresh();
        })
        .catch(this.handleErrors);
    },

    updateRelated(form) {
      this.component.service
        .update(this.person.id, form)
        .then(({ data }) => {
          this.cancelDialogAndRefresh();
        })
        .catch(this.handleErrors);
    },

    removeRelated() {
      this.component.service
        .remove(this.person.id, this.component.data.value.related_id)
        .then(({ data }) => {
          this.cancelDialogAndRefresh();
          this.$store.commit('snackbar/setSnack', {
            status: 'success',
            text: this.$t('MESSAGE.SUCCESS')
          });
        })
        .catch(this.handleErrors);
    }
  }
};
</script>
